import { Datum } from "../../../components/docs/Datum.js";
import { note, p, table } from "../blocks.js";
import { QUOTED } from "../thresholds.js";
import type { WikiTopic } from "../types.js";

export const gapDetector: WikiTopic = {
  slug: "gap-detector",
  title: "Gap detector",
  summary: "An overnight gap wider than a multiple of daily ATR, traded as a fade.",
  group: "strategies",
  horizon: "session",
  strategyKey: "gap_detector",
  sections: [
    {
      heading: "What it looks for",
      code: "gap_up / gap_down",
      blocks: [
        p(
          <>
            Bucket 1 only, because a gap is an overnight event. The reference runs on five-minute
            bars and takes its previous close from the previous candle, which on any bucket except
            the first is not an overnight anything.
          </>,
        ),
        p(
          <>
            It compares the first bucket's open against the previous session's adjusted daily close
            and scales that distance by the daily ATR-
            <Datum>{QUOTED.GAP_ATR_PERIOD}</Datum>. It fires when the gap exceeds{" "}
            <Datum>{QUOTED.GAP_MIN_ATR}×</Datum> that ATR. Below half a daily ATR an overnight move
            is noise.
          </>,
        ),
        p(
          <>
            Daily ATR, not hourly. An hourly ATR measures intraday range, and scaling an overnight
            gap against it makes a 2% gap on a quiet name and a 2% gap on a volatile one score
            identically. The daily bars arrive already adjusted, so a 1:10 split does not read as a
            90% gap, and this strategy re-adjusts nothing.
          </>,
        ),
      ],
    },
    {
      heading: "The trade is the fill, so the side inverts the type",
      blocks: [
        p(
          <>
            This is the one a reader is most likely to get backwards. <Datum>gap_up</Datum> carries
            a <Datum>short</Datum> and <Datum>gap_down</Datum> carries a <Datum>long</Datum>. The
            type names the gap's direction; the side names the trade, which is the fade back to the
            previous close. Direction-encoded types give each one its own once-a-day cooldown on the
            unique index.
          </>,
        ),
        p(
          <>
            A second guard drops a gap that already filled by the bucket's close. A fill that has
            already happened is not a trade: the entry price is the bucket close, so a gap up that
            traded back through the previous close inside the hour would carry a short whose target
            sits above its own entry. The trade metrics drop a target on the wrong side of the
            entry, which is how <Datum>CASTROLIND</Datum> on <Datum>2026-09-07</Datum> showed a
            short with no target, no R, and no progress: previous close <Datum>190.27</Datum>, open{" "}
            <Datum>191.80</Datum>, and <Datum>189.39</Datum> by 10:15. Worse, it still voted, and
            carried a strong bearish confluence row on a move that was over.
          </>,
        ),
        note(
          <>
            The unique index is not what does this work. Its cooldown is per session, so it would
            suppress a later bucket, not this one.
          </>,
        ),
      ],
    },
    {
      heading: "The gates",
      blocks: [
        table(
          "Gap detector gates",
          ["Gate", "Value"],
          [
            ["Bucket", <Datum key="k">bucket 1 only</Datum>],
            ["ATR period (daily)", <Datum key="a">{QUOTED.GAP_ATR_PERIOD}</Datum>],
            ["Minimum gap", <Datum key="g">{QUOTED.GAP_MIN_ATR}× daily ATR</Datum>],
            ["Reference close", "Previous session, adjusted"],
            ["Already filled", "Rejected"],
          ],
        ),
      ],
    },
    {
      heading: "What the row carries",
      blocks: [
        p(
          <>
            Magnitude is the gap divided by the daily ATR, so it reads as a distance in daily ATRs.
          </>,
        ),
        p(
          <>
            The target is the previous session's close, which is the fill. The stop sits half an ATR
            beyond the open on the far side from the fill: above the open on a gap up, below it on a
            gap down.
          </>,
        ),
      ],
    },
  ],
};
